/**
 * guestDb.ts — Lightweight Dexie Cloud instance for guest share pages.
 *
 * Uses the same schema as the main app (required for Dexie Cloud sync)
 * but skips Yjs addon to avoid conflicts.
 *
 * Usage:
 *   const guestDb = createGuestDb()
 *   await guestDb.cloud.login({ grant_type: "demo", userId: "share-xxx@demo.local" })
 *   // Dexie Cloud syncs the guest's shared realms → useLiveQuery works
 */

import Dexie, { type Table } from "dexie"
import dexieCloud from "dexie-cloud-addon"
import type {
  DbObject,
  DbIndex,
  DbStartAtIndex,
  DbSetting,
  DbDevice,
  DbPushToken,
  DbReminder,
  DbFile,
} from "./schema"

// ---------------------------------------------------------------------------
// Guest Dexie class — same schema, no Yjs, separate IDB name
// ---------------------------------------------------------------------------

class GuestDexie extends Dexie {
  totodo!: Table<DbObject, string>
  privateIndex!: Table<DbIndex, string>
  startAtIndex!: Table<DbStartAtIndex, string>
  setting!: Table<DbSetting, string>
  setting_local!: Table<DbSetting, string>
  debug_log!: Table<{ id?: string; description: string; createdAt: string }, string>
  import_tmp!: Table<{ id?: string; data: unknown; createdAt: string; createdBy: string }, string>
  doc_cache!: Table<{ id?: string; createdAt: string }, string>
  device!: Table<DbDevice, string>
  push_token!: Table<DbPushToken, string>
  reminder!: Table<DbReminder, string>
  files!: Table<DbFile, string>

  constructor() {
    // Separate IDB name to avoid conflicts with main app db
    super("ToToDoGuestDb", { addons: [dexieCloud], cache: "immutable" })

    // Must match main app schema exactly for Dexie Cloud sync
    this.version(80).stores({
      totodo: `id, [id+isDeleted+status], [parentPath+type], [parentPath+isDeleted], [parentPath+type+isDeleted], [parentPath+index], [type+status+isDeleted], [type+isDeleted+status], [type+isDeleted+owner], [type+isDeleted], [parentPath+status+isDeleted], type, index, title, note, *tags, dueAt, createdAt, createdBy, updatedAt, updatedBy, isDeleted, deletedAt, deletedBy, status, statusAt, statusBy, userStatus, realmId, owner, *fullTextIndex`,
      privateIndex: "id, index",
      startAtIndex: "id, index, startAt, type, subType, reminder",
      setting: "id, value, device, createdAt, createdBy, updatedAt, updatedBy",
      setting_local:
        "id, value, device, createdAt, createdBy, updatedAt, updatedBy",
      debug_log: "id, description, createdAt",
      import_tmp: "id, data, createdAt, createdBy",
      doc_cache: "id, createdAt",
      device:
        "id, platform, createdAt, updatedAt, isDeleted, deletedAt, model, operatingSystem, osVersion, iOSVersion, androidSDKVersion, manufacturer, isVirtual, memUsed, webViewVersion, appVersion, appBuild, language, timezone, lastSeenAt, nickname, status, statusAt, userAgent, loginLocation, lastSeenLocation, lastSeenLocationAt",
      push_token:
        "id, [deviceId+platform+token+status], deviceId, platform, createdAt, isDeleted, deletedAt, updatedAt, token, provider, status, errorCode, errorAt, revokedAt, lastValidatedAt",
      reminder:
        "id, [taskId+isDeleted], taskId, title, body, link, owner, realmId, triggerType, fireAt, kind, sourceType, createdAt, createdBy, updatedAt, updatedBy, isDeleted, deletedAt",
    })

    this.version(81).stores({
      files: "@id, name, mimeType, parentId, parentType, createdAt, realmId",
    })

    this.cloud.configure({
      databaseUrl:
        import.meta.env.VITE_DEXIE_CLOUD_URL || "https://zfbftppyv.dexie.cloud",

      tryUseServiceWorker: false,

      // Same unsynced tables as main app
      unsyncedTables: ["setting_local", "debug_log", "import_tmp", "doc_cache"],
      unsyncedProperties: {
        totodo: ["docHtml", "fullTextIndex"],
      },

      // Custom login GUI — we handle login programmatically
      customLoginGui: true,

      // Don't block db.open() — we login after mount
      requireAuth: false,
    })
  }
}

// ---------------------------------------------------------------------------
// Singleton — created lazily, shared across guest page lifetime
// ---------------------------------------------------------------------------

let instance: GuestDexie | null = null

export function getGuestDb(): GuestDexie {
  if (!instance) {
    instance = new GuestDexie()
  }
  return instance
}

/** Close and dispose the guest db (call on unmount) */
export async function closeGuestDb(): Promise<void> {
  if (instance) {
    instance.close()
    instance = null
  }
}
